import type { Skill_Listing } from '@oneappexchange/oaf-schema';
import type { ListingResponse } from '../services/listing_service';
import { getExternalInstallConfig } from './external_install_utils';
import { AI_PLUGIN_EXTENSION_TYPE, AI_SKILL_EXTENSION_TYPE } from './aiListingExtensionTypes';

/**
 * Helpers for reading skill-specific data off a listing.
 *
 * A skill listing carries a `listing/extensions/ai-skill/listings/Listing` extension whose `data`
 * block holds the skill's structured fields: `repoFolderUrl`, `files`, `version`, `commitSha`,
 * `publishedDate`, `lastModifiedDate`, `domains`. The publishing pipeline captures these at ingest. The block
 * also carries `skillmd` (the SKILL.md verbatim), which primarily exists FOR SEARCH. Every other
 * Package Files body is fetched through the BFF like any other file, but SKILL.md is the one
 * exception: it's also the tab's default selection, and its full content already rides along on
 * the listing, so `getSkillMarkdown` doubles as that file's UI content source (TEMP-SKILL-POC,
 * W-23546079 — see `skillPackageFiles.ts`'s pre-seed of its body cache). The other value still read
 * out of `skillmd` is `compatibility`, which has no structured field yet.
 */

/**
 * Reads the skill extension's `data` block off a listing, or null when the listing carries no
 * skill extension. The single reader every skill getter below funnels through.
 */
function getSkillExtensionData(listing?: ListingResponse | null): Skill_Listing | null {
    // A listing should carry at most one skill extension; `find` deterministically keeps the first
    // if ingest ever duplicates the type, rather than picking whichever sorts last.
    const extension = listing?.extensions?.find((candidate) => candidate.extensionType === AI_SKILL_EXTENSION_TYPE);
    return (extension?.data as Skill_Listing | undefined) ?? null;
}

/**
 * The skill's SKILL.md content, or '' when the listing carries no skill extension.
 *
 * Feeds the compatibility front-matter parse below, AND — TEMP-SKILL-POC (W-23546079) —
 * `skillPackageFiles.ts` pre-seeds SKILL.md's body-cache entry from this getter, since SKILL.md's
 * full verbatim content already rides along on the listing and is the tab's default selection.
 * Every other Package Files body still comes from a BFF/GitHub fetch; only SKILL.md sources from
 * here.
 */
export function getSkillMarkdown(listing?: ListingResponse | null): string {
    return getSkillExtensionData(listing)?.skillmd ?? '';
}

/** The optional, hosted screenshot is listing metadata, separate from the installed skill files. */
export function getSkillPreviewImageUrl(listing?: ListingResponse | null): string {
    // Temporary until the published oaf-schema includes Skill_Listing.imageUrl.
    const data = getSkillExtensionData(listing) as (Skill_Listing & { imageUrl?: unknown }) | null;
    return parseHttpsUrl(data?.imageUrl);
}

// Front matter must open on the very first line; anything else is a body-level `---` rule. Group 1
// is the block's contents. Everything after the closing fence — trailing spaces, then the blank
// lines separating the fence from the document — is matched too but left out of the group, so
// replacing a match strips the block cleanly and leaves the body starting at its first character.
const FRONT_MATTER_PATTERN = /^---\r?\n([\s\S]*?)\r?\n---[^\S\r\n]*(?:\r?\n)*/;

/**
 * The raw YAML front-matter block of a SKILL.md (the text between the opening and closing `---`
 * fences), or '' when the document has no front matter.
 *
 * Deliberately not a YAML parse: the front matter is only read for a couple of scalar values, and
 * pulling in a YAML dependency to serve two getters isn't worth the bundle cost.
 * `getFrontMatterValue` handles the scalar shapes these documents actually use.
 */
function getFrontMatter(markdown: string): string {
    return markdown.match(FRONT_MATTER_PATTERN)?.[1] ?? '';
}

// Escapes regex metacharacters so a key can be interpolated into `new RegExp` as a literal match
// rather than a pattern — load-bearing once `key` is anything other than a hardcoded call-site
// string.
function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Reads one top-level scalar value out of a SKILL.md front-matter block by key.
 *
 * Only top-level keys are supported (`compatibility`), which covers every key the live skills use.
 * Handles the quoting the real documents contain: values may be bare, single-quoted, or
 * double-quoted. Returns '' when the key is absent or has no inline value.
 */
function getFrontMatterValue(markdown: string, key: string): string {
    const frontMatter = getFrontMatter(markdown);
    if (!frontMatter) {
        return '';
    }

    const pattern = new RegExp(`^${escapeRegExp(key)}\\s*:`);
    const line = frontMatter.split(/\r?\n/).find((candidate) => pattern.test(candidate));
    if (!line) {
        return '';
    }

    const value = line.slice(line.indexOf(':') + 1).trim();
    // Strip a single matching pair of surrounding quotes, if present.
    return value.replace(/^(['"])([\s\S]*)\1$/, '$2').trim();
}

/**
 * The SKILL.md with its YAML front matter removed — what a *rendered* view of the document should
 * show.
 *
 * A markdown renderer has no concept of front matter, so handing it the raw `skillmd` renders
 * `name: … description: … metadata: version: …` as a prose paragraph above the document's real
 * heading. The front matter is metadata for tooling (and the source of the version/compatibility
 * getters above), not content, so the preview strips it. Views that show the *file* rather than a
 * rendering of it — the code view, the raw copy — keep using `getSkillMarkdown` verbatim.
 *
 * Returns the input unchanged when there is no front matter to strip.
 */
export function getSkillMarkdownBody(listing?: ListingResponse | null): string {
    return stripFrontMatter(getSkillMarkdown(listing));
}

/**
 * Strips a leading YAML front-matter block from any markdown string, returning the input unchanged
 * when there is none. Split out from `getSkillMarkdownBody` so callers that render a markdown
 * file's body directly — the Package Files viewer's Preview mode, whether that body came from a
 * GitHub fetch or (for SKILL.md) the listing's `skillmd` — get the same front-matter handling
 * without going through the listing-specific getters above.
 */
export function stripFrontMatter(markdown: string): string {
    return markdown.replace(FRONT_MATTER_PATTERN, '');
}

/**
 * The skill's version (e.g. '0.8'), from the structured `version` field on the skill extension.
 *
 * Returns '' when absent, which callers treat as "not available".
 */
export function getSkillVersion(listing?: ListingResponse | null): string {
    return getSkillExtensionData(listing)?.version ?? '';
}

/**
 * The date the skill was first listed (YYYY-MM-DD), from the structured `publishedDate` field —
 * the first GHE commit that touched the skill's folder. '' when absent.
 */
export function getSkillPublishedDate(listing?: ListingResponse | null): string {
    return getSkillExtensionData(listing)?.publishedDate ?? '';
}

/**
 * The date the skill's folder was most recently changed (YYYY-MM-DD), from the structured
 * `lastModifiedDate` field. '' when absent.
 *
 * The publishing pipeline uses this in place of the base listing's `user_updated_at`, which is
 * tagged `sensitivityLevel: confidential` and scrubbed from `/experience` responses.
 */
export function getSkillLastModifiedDate(listing?: ListingResponse | null): string {
    return getSkillExtensionData(listing)?.lastModifiedDate ?? '';
}

/**
 * The skill's SkillDomain values (e.g. ['agentforce', 'service']), from the structured `domains`
 * field — synced by the repo pipeline from SKILL.md's `metadata.domains` (W-23803699). Returns []
 * when absent.
 */
export function getSkillDomains(listing?: ListingResponse | null): string[] {
    const domains: unknown = getSkillExtensionData(listing)?.domains;
    return Array.isArray(domains)
        ? domains.filter((domain): domain is string => typeof domain === 'string' && domain.length > 0)
        : [];
}

/**
 * Related skill external ids (for example `skill-agentforce-observe`) from the structured skill
 * extension. OAF validates canonical ids and rejects duplicates; this reader still guards the
 * ahead-of-schema field at runtime so an older or malformed response degrades to no section.
 */
export function getRelatedSkillListingIds(listing?: ListingResponse | null): string[] {
    const relatedSkills = getSkillExtensionData(listing)?.relatedSkills;
    if (!Array.isArray(relatedSkills)) {
        return [];
    }
    return relatedSkills.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * The plugins this skill belongs to (e.g. ['plugin-agentforce']). Feeds the skill's "Found In" tab.
 * The experience API returns this derived relationship at the listing's top level. Returns []
 * when absent.
 */
export function getParentPluginListingIds(listing?: ListingResponse | null): string[] {
    const ids = listing?.parentPluginListingIds;
    return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];
}

/**
 * The skill's opted-in file list — folder-relative paths (e.g. 'SKILL.md',
 * 'assets/agents/hello-world.agent'). The publisher opts a file in by including it and out by
 * excluding it. Returns [] when absent.
 *
 * These are the leaves of the skill's file tree; `buildSkillFileTree` synthesizes the intermediate
 * directory nodes from them.
 */
export function getSkillFiles(listing?: ListingResponse | null): string[] {
    const files = getSkillExtensionData(listing)?.files;
    return Array.isArray(files)
        ? files.filter((file): file is string => typeof file === 'string' && file.length > 0)
        : [];
}

/**
 * The skill's compatibility statement, from `compatibility` in the SKILL.md front matter
 * (e.g. 'Requires Agentforce license, API v66.0+, Einstein Agent User').
 *
 * Optional in practice — only some skills declare it — so callers must handle ''.
 */
export function getSkillCompatibility(listing?: ListingResponse | null): string {
    return getFrontMatterValue(getSkillMarkdown(listing), 'compatibility');
}

// ============================================================================
// Supported harnesses + install methods (install card CTAs).
// ============================================================================

/**
 * One harness (AI coding tool) a skill can be installed into, with the install methods OAF
 * publishes for it. `installMethodId` is the site's existing id for the harness — the Skills CLI
 * `-a` slug, the persisted dropdown cookie value, and the analytics key — so the schema's
 * `harnessId` never leaks into any of those contracts.
 */
export interface SkillHarness {
    installMethodId: string;
    hasPrompt: boolean;
    hasCli: boolean;
    zipUrl: string; // '' when the harness has no ZIP method
}

// OAF `harnessId` → site install-method id. Harnesses the site has no label or CLI slug for are
// dropped rather than rendered as an unnamed option.
const HARNESS_INSTALL_METHOD_IDS: Record<string, string> = {
    agentforceVibes: 'agentforce-vibes',
    claude: 'claude-code',
    cursor: 'cursor',
    chatGpt: 'codex',
    openCode: 'opencode',
};

// The install card's behavior before OAF published `supportedHarnesses`: every harness offers the
// prompt + CLI methods, except Agentforce Vibes, which installs SF Skills automatically.
const LEGACY_SKILL_HARNESSES: SkillHarness[] = [
    {
        installMethodId: 'agentforce-vibes',
        hasPrompt: false,
        hasCli: false,
        zipUrl: '',
    },
    { installMethodId: 'claude-code', hasPrompt: true, hasCli: true, zipUrl: '' },
    { installMethodId: 'cursor', hasPrompt: true, hasCli: true, zipUrl: '' },
    { installMethodId: 'codex', hasPrompt: true, hasCli: true, zipUrl: '' },
    { installMethodId: 'opencode', hasPrompt: true, hasCli: true, zipUrl: '' },
];

type InstallMethodData = { provider?: unknown; params?: { url?: unknown } };
type SupportedHarnessData = { harnessId?: unknown; installMethods?: unknown };

// Preview images may use any absolute HTTPS URL without embedded credentials.
function parseHttpsUrl(value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }
    try {
        const parsed = new URL(value.trim());
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password) {
            return '';
        }
        return parsed.href;
    } catch {
        return '';
    }
}

// ZIP downloads are supported only from our approved template release repository. This reader
// is shared by the UI and download service, so unsupported URLs never advertise a download CTA.
function parseZipUrl(value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }
    try {
        const parsed = new URL(value.trim());
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password || parsed.search || parsed.hash) {
            return '';
        }
        const apiAsset =
            parsed.host === 'api.github.com' &&
            /^\/repos\/OneAppExchange\/aiforce-template-skills\/releases\/assets\/[1-9][0-9]*$/i.test(parsed.pathname);
        const browserAsset =
            parsed.host === 'github.com' &&
            /^\/OneAppExchange\/aiforce-template-skills\/releases\/download\/[^/]+\/[^/]+\.zip$/i.test(parsed.pathname);
        return apiAsset || browserAsset ? parsed.href : '';
    } catch {
        return '';
    }
}

function toSkillHarness(data: SupportedHarnessData): SkillHarness | null {
    const installMethodId = typeof data?.harnessId === 'string' ? HARNESS_INSTALL_METHOD_IDS[data.harnessId] : '';
    if (!installMethodId) {
        return null;
    }
    const methods = (Array.isArray(data.installMethods) ? data.installMethods : []) as InstallMethodData[];
    const zipMethod = methods.find((method) => method?.provider === 'zip');
    return {
        installMethodId,
        hasPrompt: methods.some((method) => method?.provider === 'prompt'),
        hasCli: methods.some((method) => method?.provider === 'cli'),
        zipUrl: parseZipUrl(zipMethod?.params?.url),
    };
}

// Skills and plugins publish `supportedHarnesses` on their own extension with the same shape.
function getSupportedHarnessesData(listing?: ListingResponse | null): unknown {
    const extension = listing?.extensions?.find(
        (candidate) =>
            candidate.extensionType === AI_SKILL_EXTENSION_TYPE || candidate.extensionType === AI_PLUGIN_EXTENSION_TYPE,
    );
    return (extension?.data as { supportedHarnesses?: unknown } | undefined)?.supportedHarnesses;
}

/**
 * The harnesses a skill or plugin can be installed into and the install methods each supports, read
 * from its extension's `supportedHarnesses` (publishing-service W-24564133). OAF owns which methods a
 * skill offers — e.g. ZIP for templates, none for sf-skills — so the install card renders exactly
 * what is published here.
 *
 * Listings published before the field existed (`supportedHarnesses` absent) keep the legacy
 * prompt + CLI behavior. Unknown harnesses are dropped; the first occurrence of a duplicated
 * harness wins.
 */
export function getSkillSupportedHarnesses(listing?: ListingResponse | null): SkillHarness[] {
    const supportedHarnesses = getSupportedHarnessesData(listing);
    if (!Array.isArray(supportedHarnesses)) {
        return LEGACY_SKILL_HARNESSES;
    }
    const harnesses: SkillHarness[] = [];
    for (const data of supportedHarnesses as SupportedHarnessData[]) {
        const harness = toSkillHarness(data);
        if (harness && !harnesses.some((existing) => existing.installMethodId === harness.installMethodId)) {
            harnesses.push(harness);
        }
    }
    return harnesses;
}

export type SkillInstallOption = 'zip' | 'prompt' | 'cli' | 'desktop';

/** The actions actually available for this skill and harness, including the legacy data fallback. */
export function getSkillInstallOptions(
    listing: ListingResponse | null | undefined,
    installMethodId: string,
): SkillInstallOption[] {
    if (getExternalInstallConfig(listing).isExternal) {
        return [];
    }
    const harness = getSkillSupportedHarnesses(listing).find((item) => item.installMethodId === installMethodId);
    if (!harness) {
        return [];
    }
    const options: SkillInstallOption[] = [];
    if (harness.zipUrl && listing?.external_id) {
        options.push('zip');
    }
    const command =
        installMethodId === 'agentforce-vibes'
            ? ''
            : buildSkillsInstallCommand(getSkillSource(listing), getSkillSlug(listing), installMethodId);
    if (harness.hasPrompt && command) {
        options.push('prompt');
    }
    if (harness.hasCli && command) {
        options.push('cli');
    }
    if (options.includes('zip') && installMethodId === 'claude-code') {
        options.push('desktop');
    }
    return options;
}

/**
 * The install-method ids a skill or plugin explicitly lists in `supportedHarnesses`, for the metadata
 * card's "Works with" row. [] when the field is absent — unlike `getSkillSupportedHarnesses`, the legacy
 * fallback is not a published compatibility claim.
 */
export function getSkillWorksWithInstallMethodIds(listing?: ListingResponse | null): string[] {
    return Array.isArray(getSupportedHarnessesData(listing))
        ? getSkillSupportedHarnesses(listing).map((harness) => harness.installMethodId)
        : [];
}

// ============================================================================
// Skill source coordinates + file-tree shape (Package Files viewer).
// ============================================================================

/**
 * A skill's source-repo coordinates, parsed from the listing's `repoFolderUrl` and pinned to a
 * commit SHA.
 *
 * The skill listing carries the folder's GitHub Enterprise tree URL (`repoFolderUrl`) plus the
 * `commitSha` it was ingested at. `getSkillSource` parses those into these coordinates so the BFF
 * can fetch file bodies from GHE, and the viewer can offer a "view on GitHub" link. `ref` is the
 * commit every file body is read at, so the viewer stays commit-consistent.
 */
export interface SkillSource {
    host: string; // GHE host, e.g. 'git.soma.salesforce.com'
    owner: string; // e.g. 'hsinghbisht'
    repo: string; // e.g. 'sf-skills-internal'
    path: string; // the package folder within the repo, or '' when the package is the repo root
    ref: string; // commit SHA the file bodies are pinned to
    url: string; // the repoFolderUrl verbatim (breadcrumb / "view on GitHub" affordance)
}

// Keep every generated install surface on one reviewed CLI release. An exact package version is
// intentional: `npx skills` otherwise resolves the mutable latest tag at execution time.
export const SKILLS_CLI_PACKAGE = 'skills@1.5.22';
const SKILLS_INSTALL_BRANCH = 'main';
const PUBLIC_GITHUB_HOST = 'github.com';
const SAFE_REPOSITORY_HOST_PATTERN = /^[A-Za-z0-9.-]+(?::[0-9]+)?$/;

export interface RepositoryMarkdownUrls {
    linkBaseUrl: string;
    imageBaseUrl: string;
    allowedImageRootUrls: string[];
}

function isSafeRepositoryHost(host: string): boolean {
    return SAFE_REPOSITORY_HOST_PATTERN.test(host);
}

/**
 * Builds commit-pinned Markdown URL boundaries for a repository directory.
 *
 * Links resolve through the repository's blob view. Images resolve through its raw view and may
 * only survive sanitization when they remain under one of the returned roots for this exact
 * owner/repository/ref. Public GitHub's raw-content host is included because GitHub commonly emits
 * that equivalent absolute form; arbitrary hosts, repositories, branches, and commits are absent.
 */
export function buildRepositoryMarkdownUrls(source: SkillSource | null, directoryPath: string): RepositoryMarkdownUrls {
    if (!source || !isSafeRepositoryHost(source.host)) {
        return { linkBaseUrl: '', imageBaseUrl: '', allowedImageRootUrls: [] };
    }

    const owner = encodeURIComponent(source.owner);
    const repo = encodeURIComponent(source.repo);
    const ref = encodeURIComponent(source.ref);
    const repositoryUrl = `https://${source.host}/${owner}/${repo}`;
    const rawRootUrl = `${repositoryUrl}/raw/${ref}/`;
    const blobRootUrl = `${repositoryUrl}/blob/${ref}/`;
    const directory = directoryPath
        .split('/')
        .filter(Boolean)
        .map((segment) => encodeURIComponent(segment))
        .join('/');
    const directorySuffix = directory ? `${directory}/` : '';
    const allowedImageRootUrls = [rawRootUrl, blobRootUrl];

    if (source.host.toLowerCase() === PUBLIC_GITHUB_HOST) {
        allowedImageRootUrls.push(`https://raw.githubusercontent.com/${owner}/${repo}/${ref}/`);
    }

    return {
        linkBaseUrl: `${blobRootUrl}${directorySuffix}`,
        imageBaseUrl: `${rawRootUrl}${directorySuffix}`,
        allowedImageRootUrls,
    };
}

/**
 * The skill's canonical slug — the identifier the `npx skills add` CLI expects (e.g.
 * 'agentforce-generate'), which is the basename of the skill's source folder path
 * (`skills/agentforce-generate` → `agentforce-generate`). Returns '' when the listing carries no
 * parseable source.
 *
 * Derived from the source folder, NOT the listing's `name`: `name` is the human-readable display
 * title ('Agentforce Generate'), which is not a valid CLI slug. The folder name is the slug.
 */
export function getSkillSlug(listing?: ListingResponse | null): string {
    const path = parseRepoFolderUrl(getSkillExtensionData(listing)?.repoFolderUrl)?.path ?? '';
    const slug = path.split('/').filter(Boolean).pop() ?? '';
    return isSafeInstallerSegment(slug) ? slug : '';
}

// Values returned by the helpers above are interpolated into a shell command displayed to the
// user. Repository and package names only need the portable Git/GitHub identifier character set;
// reject everything else so authored source metadata cannot add shell operators, substitutions,
// or option-like arguments to the generated command.
export function isSafeInstallerSegment(value: string): boolean {
    return /^[A-Za-z0-9._-]+$/.test(value) && !value.startsWith('-') && value !== '.' && value !== '..';
}

/**
 * Parses a GitHub (Enterprise or public) tree URL into its coordinates, or null when the URL
 * doesn't match the `.../{owner}/{repo}/tree/{ref}[/{path...}]` shape or isn't `https:`.
 *
 * The optional `path` can contain slashes (nested folders), so it's captured greedily as the rest
 * of the URL after `/tree/{ref}/`. An absent path represents a package rooted at the repository;
 * a trailing slash on either shape is tolerated. Returns null (rather than throwing) so callers
 * degrade to "no source" gracefully on a malformed or absent URL. The `https:` requirement matters
 * beyond parsing correctness: the result's `url` is bound verbatim into an `href` by
 * skillSidebar/pluginSidebar/guidedInstallModal, so a non-`https:` scheme (e.g. `javascript:`) must
 * never parse successfully here. `ref` is returned as-is — it's the URL's own segment, which may or
 * may not end up used (see `resolveSourceRef` below).
 */
export function parseRepoFolderUrl(
    url?: string | null,
): { host: string; owner: string; repo: string; ref: string; path: string } | null {
    if (!url) {
        return null;
    }
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }
    if (parsed.protocol !== 'https:' || !isSafeRepositoryHost(parsed.host)) {
        return null;
    }
    // URL.pathname retains percent escapes. Decode each route segment once here so downstream URL
    // builders can safely encode the resolved coordinates once, rather than turning `%20` into
    // `%2520`. A malformed escape sequence makes the source unusable and therefore fails closed.
    let segments: string[];
    try {
        segments = parsed.pathname.split('/').filter(Boolean).map(decodeURIComponent);
    } catch {
        return null;
    }
    if (segments.length < 4 || segments[2] !== 'tree') {
        return null;
    }
    const [owner, repo, , ref, ...pathSegments] = segments;
    const path = pathSegments.join('/');
    if (!owner || !repo || !ref) {
        return null;
    }
    return { host: parsed.host, owner, repo, ref, path };
}

/**
 * Joins a package's repository-relative root with one of its manifest-relative paths. Root-level
 * packages use an empty `rootPath`, so a plain template literal would incorrectly create an
 * absolute-looking `/README.md`; keeping this join in one helper prevents the client tree and BFF
 * GitHub request from disagreeing about the file coordinate.
 */
export function joinRepositoryPath(rootPath: string, relativePath: string): string {
    return rootPath ? `${rootPath}/${relativePath}` : relativePath;
}

// SkillSource.ref must stay a fixed commit pin (see the interface doc above) — oaf-schema
// documents both Skill_Listing.repoFolderUrl and Plugin_Listing.repoFolderUrl as "SHA-pinned"
// (`.../tree/{sha}/{folderPath}`), so the URL's own ref segment is expected to be a commit SHA too.
// commitSha is trusted, structured ingest data and always wins when present; the URL's ref is only
// a fallback for when commitSha is absent, so it's only honored when it's SHA-shaped — a branch
// name there would break the "commit-consistent" guarantee (its contents can move under a ref this
// code would otherwise cache as if it were fixed).
const COMMIT_SHA_PATTERN = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/i;

/**
 * Resolves a `SkillSource`'s `ref`: prefers `commitSha`, falling back to the folder URL's own ref
 * segment only when it's commit-SHA-shaped (see `COMMIT_SHA_PATTERN` above). Returns '' when
 * neither yields a usable pin. Shared by `getSkillSource` and `getPluginSource`.
 */
export function resolveSourceRef(commitSha: string | undefined, urlRef: string): string {
    if (commitSha && COMMIT_SHA_PATTERN.test(commitSha)) {
        return commitSha;
    }
    return COMMIT_SHA_PATTERN.test(urlRef) ? urlRef : '';
}

/**
 * Returns the canonical tree URL for a parsed listing source, using the ref supplied by its caller.
 * The original `repoFolderUrl` can contain a branch even when ingest also supplied a commit SHA,
 * so generated URLs must be rebuilt from the resolved `ref` rather than reusing `source.url`.
 */
export function getPinnedSourceUrl(source: SkillSource | null): string {
    if (!source || !isSafeRepositoryHost(source.host)) {
        return '';
    }
    const owner = encodeURIComponent(source.owner);
    const repo = encodeURIComponent(source.repo);
    const path = source.path
        .split('/')
        .filter(Boolean)
        .map((segment) => encodeURIComponent(segment))
        .join('/');
    const pathSuffix = path ? `/${path}` : '';
    return `https://${source.host}/${owner}/${repo}/tree/${source.ref}${pathSuffix}`;
}

/**
 * Builds the install-command shape used by the sidebar and guided prompt.
 *
 * The Skills CLI accepts `owner/repository` shorthand for skills on public GitHub, which keeps
 * those commands concise. Enterprise-hosted sources must remain host-qualified or the shorthand
 * would resolve against public github.com. `npx --yes` suppresses npm's package-download
 * confirmation when the pinned CLI is not cached; these commands are pasted into coding agents,
 * whose shell tools cannot reliably answer interactive prompts. The trailing Skills CLI `--yes`
 * suppresses its own install confirmations. Plugins intentionally do not use this helper: Claude
 * plugins must be installed as marketplace packages with `claude plugin install`. Returning '' for
 * an absent source fails closed.
 */
export function buildSkillsInstallCommand(source: SkillSource | null, slug: string, installMethodId: string): string {
    if (
        !source ||
        !isSafeRepositoryHost(source.host) ||
        !isSafeInstallerSegment(installMethodId) ||
        !isSafeInstallerSegment(slug)
    ) {
        return '';
    }
    if (!isSafeInstallerSegment(source.owner) || !isSafeInstallerSegment(source.repo)) {
        return '';
    }
    const canUseRepositoryShorthand = source.host.toLowerCase() === PUBLIC_GITHUB_HOST;
    const installSource = canUseRepositoryShorthand
        ? `${source.owner}/${source.repo}`
        : getPinnedSourceUrl({ ...source, ref: SKILLS_INSTALL_BRANCH });
    return `npx --yes ${SKILLS_CLI_PACKAGE} add ${installSource} --skill ${slug} -a ${installMethodId} --yes`;
}

/**
 * Builds the bounded installed-skill check used by the guided prompt.
 *
 * The Skills CLI restricts discovery to its fixed set of supported skill directories, including
 * shared `.agents/skills` locations that an agent may miss if it guesses product-specific paths.
 * Current versions can also report existing directories for other agents, so the pasted prompt
 * requires an exact skill-name, scope, and agent match and ignores all other records. This remains
 * bounded and avoids an expensive home-directory search. `npx --yes` also keeps the check
 * non-interactive when the pinned CLI package is not cached yet.
 */
export function buildSkillsListCommand(installMethodId: string, global = false): string {
    if (!isSafeInstallerSegment(installMethodId)) {
        return '';
    }
    const scopeFlag = global ? ' --global' : '';
    return `npx --yes ${SKILLS_CLI_PACKAGE} list${scopeFlag} -a ${installMethodId} --json`;
}

/**
 * Reads a skill listing's source-repo coordinates from its `repoFolderUrl` (+ `commitSha`), or null
 * when the listing carries no skill extension, an unparseable URL, or no usable ref (see
 * `resolveSourceRef` above).
 *
 * `ref` prefers the structured `commitSha`; it falls back to the ref embedded in the URL when
 * `commitSha` is absent, so a URL alone still yields a usable source. `url` keeps the original
 * `repoFolderUrl` verbatim for display.
 */
export function getSkillSource(listing?: ListingResponse | null): SkillSource | null {
    const data = getSkillExtensionData(listing);
    const parsed = parseRepoFolderUrl(data?.repoFolderUrl);
    if (!parsed) {
        return null;
    }
    const ref = resolveSourceRef(data?.commitSha, parsed.ref);
    if (!ref) {
        return null;
    }
    return {
        host: parsed.host,
        owner: parsed.owner,
        repo: parsed.repo,
        path: parsed.path,
        ref,
        url: data!.repoFolderUrl!,
    };
}

// Skill repos under the OneAppExchange GitHub org (e.g. the AI templates) are private, so links to
// them 404 for visitors. Listings carry no visibility flag, so the owning org is the signal.
const PRIVATE_SKILL_SOURCE_OWNERS = new Set(['oneappexchange']);

export function isPrivateSkillSource(source?: SkillSource | null): boolean {
    return !!source && PRIVATE_SKILL_SOURCE_OWNERS.has(source.owner.toLowerCase());
}

/**
 * One node in a skill's file tree — a file or a directory. Directories carry `children`; files may
 * carry a lazily-fetched `body`. Matches the shape the file-tree component and the Package Files
 * viewer consume.
 */
export interface SkillFileNode {
    path: string; // full repo-relative path, unique — usable as a stable key
    name: string; // the leaf name (file or directory), no path
    type: 'file' | 'dir';
    ext?: string; // file extension without the dot, for files (e.g. 'md', 'yaml')
    body?: string; // file contents, populated lazily on selection
    children?: SkillFileNode[]; // directory contents
}

/**
 * Sorts a SkillFileNode array in place — directories first, then files, each group alphabetically
 * by name (case-insensitive) — and recurses into directory children.
 */
function sortSkillFileNodes(nodes: SkillFileNode[]): void {
    nodes.sort((a, b) => {
        if (a.type !== b.type) {
            return a.type === 'dir' ? -1 : 1;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    for (const node of nodes) {
        if (node.children) {
            sortSkillFileNodes(node.children);
        }
    }
}

/**
 * Builds a nested `SkillFileNode[]` from a skill's flat, folder-relative file list (the listing's
 * `files`), rooted at `rootPath` (the skill folder, e.g. 'skills/agentforce-generate').
 *
 * The `files` list is LEAF-ONLY — it names files, not the directories above them — so this
 * synthesizes the intermediate directory nodes on the way down, deduplicating them across files
 * that share an ancestor. Every node's `path` is the FULL repo-relative path (`${rootPath}/${rel}`)
 * so it matches the coordinates the BFF fetches bodies at. Each level is sorted directories-first,
 * then alphabetically. Empty/blank entries are skipped.
 *
 * @param files Folder-relative file paths (e.g. ['SKILL.md', 'assets/agents/hello-world.agent'])
 * @param rootPath The skill's folder within the repo
 * @returns Nested SkillFileNode[] — the direct children of `rootPath`
 */
export function buildSkillFileTree(files: string[], rootPath: string): SkillFileNode[] {
    const rootNodes: SkillFileNode[] = [];
    // Directory node by full path, so ancestors are created once and reused across sibling files.
    const dirMap = new Map<string, SkillFileNode>();

    for (const relativePath of files) {
        // Drop empty AND whitespace-only segments defensively; real paths have neither, but a stray
        // '' or '  ' entry must not become a phantom node.
        const segments = relativePath.split('/').filter((segment) => segment.trim().length > 0);
        if (segments.length === 0) {
            continue;
        }

        // Walk the segments, creating/reusing a directory node for each non-leaf segment and a file
        // node for the leaf. `parentChildren` tracks where the next node is placed.
        let parentChildren = rootNodes;
        let accumulatedPath = rootPath;

        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            accumulatedPath = joinRepositoryPath(accumulatedPath, segment);
            const isLeaf = i === segments.length - 1;

            if (isLeaf) {
                const extMatch = segment.match(/\.([^.]+)$/);
                parentChildren.push({
                    path: accumulatedPath,
                    name: segment,
                    type: 'file',
                    ext: extMatch ? extMatch[1].toLowerCase() : undefined,
                });
            } else {
                let dirNode = dirMap.get(accumulatedPath);
                if (!dirNode) {
                    dirNode = { path: accumulatedPath, name: segment, type: 'dir', children: [] };
                    dirMap.set(accumulatedPath, dirNode);
                    parentChildren.push(dirNode);
                }
                parentChildren = dirNode.children!;
            }
        }
    }

    sortSkillFileNodes(rootNodes);
    return rootNodes;
}
